import { isCampaignSeller, isCampaignManufacturer, isCampaignDropShipping } from '../../../utils/Utils';
import { isMarketplaceOperatorCampaign } from '../../Campaign/marketplaceOperatorSettings';

export const PROFILE_KEYS = {
  COMPETITIVE: 'competitive',
  BRAND: 'brand',
  DROPSHIPPING: 'dropshipping',
  MARKET: 'market',
  MARKETPLACE: 'marketplace',
};

// Single source of truth. Each detail decision reads from here.
const PROFILES = {
  [PROFILE_KEYS.COMPETITIVE]: {
    key: PROFILE_KEYS.COMPETITIVE,
    accent: '#2563eb',
    heroSignals: ['pricePosition', 'stock', 'reprice'],
    columnGroups: ['identity', 'classification', 'price', 'competition', 'reviews', 'variant'],
    filterGroups: ['status', 'competition', 'supplier', 'template', 'classification', 'priceRange'],
    quickFilters: [],
    cardFields: ['pricePosition', 'priceBand', 'suggestion'],
    bulkActions: ['activeToggle', 'tag', 'classify', 'move', 'strategy', 'delete'],
  },
  [PROFILE_KEYS.BRAND]: {
    key: PROFILE_KEYS.BRAND,
    accent: '#dc2626',
    heroSignals: ['mapViolation', 'mrpViolation', 'unauthorized'],
    columnGroups: ['identity', 'classification', 'compliance', 'price', 'reviews', 'variant'],
    filterGroups: ['status', 'sellers', 'supplier', 'compliance', 'classification', 'priceRange'],
    quickFilters: [],
    cardFields: ['violationPills', 'allowedBand', 'worstViolator'],
    bulkActions: ['activeToggle', 'tag', 'classify', 'move', 'delete'],
  },
  [PROFILE_KEYS.DROPSHIPPING]: {
    key: PROFILE_KEYS.DROPSHIPPING,
    accent: '#0891b2',
    heroSignals: ['profitLoss', 'pricePosition', 'stock'],
    columnGroups: ['identity', 'classification', 'price', 'competition', 'dropshipping', 'reviews', 'variant'],
    filterGroups: ['status', 'competition', 'supplier', 'dropshipping', 'template', 'classification', 'priceRange'],
    quickFilters: ['loss'],
    cardFields: ['profitLoss', 'price'],
    bulkActions: ['activeToggle', 'tag', 'classify', 'move', 'strategy', 'delete'],
  },
  [PROFILE_KEYS.MARKET]: {
    key: PROFILE_KEYS.MARKET,
    accent: '#5b6472',
    heroSignals: ['stock'],
    columnGroups: ['identity', 'classification', 'price', 'reviews', 'variant'],
    filterGroups: ['status', 'sellers', 'supplier', 'classification', 'priceRange'],
    quickFilters: [],
    cardFields: ['price', 'stock'],
    bulkActions: ['activeToggle', 'tag', 'classify', 'move', 'delete'],
  },
  // Marketplace operator: the list shows the operator's own marketplace against
  // the competing marketplaces. Marketplace columns come from the profile screen.
  [PROFILE_KEYS.MARKETPLACE]: {
    key: PROFILE_KEYS.MARKETPLACE,
    accent: '#2563eb',
    heroSignals: [],
    columnGroups: ['identity', 'classification'],
    filterGroups: ['status', 'marketplace', 'classification'],
    quickFilters: [],
    cardFields: [],
    bulkActions: [],
  },
};

export const getProfile = (campaign) => {
  if (isMarketplaceOperatorCampaign(campaign)) return PROFILES[PROFILE_KEYS.MARKETPLACE];
  if (isCampaignSeller(campaign)) return PROFILES[PROFILE_KEYS.COMPETITIVE];
  if (isCampaignManufacturer(campaign)) return PROFILES[PROFILE_KEYS.BRAND];
  if (isCampaignDropShipping(campaign)) return PROFILES[PROFILE_KEYS.DROPSHIPPING];
  return PROFILES[PROFILE_KEYS.MARKET]; // market search + unknown fallback
};

// Columns a profile must not render even when they are "always" columns or sit in a
// saved column selection. The marketplace profile shows prices only through its own
// columns (own offer, other marketplaces): the flat seller aggregates mix every seller
// row, including the operator's own marketplace sellers, and the own price / repricing
// columns do not apply to an operator.
const HIDDEN_COLUMNS = {
  [PROFILE_KEYS.MARKETPLACE]: [
    'competitorsRange', 'competitorsCount',
    'price', 'my_stock', 'pricing_strategy_min_value', 'pricing_strategy_max_value',
    'target_price', 'suggested_price', 'discount_type',
    'competitorsMinName', 'competitorsMinPrice', 'competitorsMinShippingPrice',
    'competitorsAvgPrice', 'competitorsAvgPriceDiff',
    'competitorsMaxName', 'competitorsMaxPrice', 'competitorsMaxShippingPrice',
    'url_count',
  ],
};

export const hiddenColumnsForProfile = (profileKey) => HIDDEN_COLUMNS[profileKey] || [];

export const isColumnAllowedForProfile = (profileKey, columnValue) =>
  !hiddenColumnsForProfile(profileKey).includes(columnValue);
